import { createContext, useContext, useEffect, useState } from 'react';
import { apiClient, AUTH_BASE } from '../api';
import { PERMISSION_PAGES_MAP } from '../config/permissions';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [pageAccess, setPageAccess] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    try {
      const res = await apiClient.get(`${AUTH_BASE}/api/auth/my-permissions`);
      const perms = res.data.permissions || [];
      setPermissions(perms);
      const pages = [];
      perms.forEach(p => {
        const mapped = PERMISSION_PAGES_MAP[p];
        if (mapped) pages.push(...mapped);
      });
      setPageAccess([...new Set(pages)]);
    } catch {
      setPermissions([]);
      setPageAccess([]);
    }
  };

  useEffect(() => {
    // Sessions used to live in localStorage; drop any token left from then so it
    // is not sitting around where scripts can read it.
    try { localStorage.removeItem('auth_token'); } catch { /* storage unavailable */ }

    const init = async () => {
      try {
        const res = await apiClient.get(`${AUTH_BASE}/api/me`);
        setUser(res.data.user ?? res.data);
        setIsAuthenticated(true);
        await fetchPermissions();
      } catch {
        setUser(null);
        setPermissions([]);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const login = () => {
    const frontendOrigin = window.location.origin;
    window.location.href = `${AUTH_BASE}/auth/login?frontend=${encodeURIComponent(frontendOrigin)}`;
  };

  const logout = async () => {
    setUser(null);
    setPermissions([]);
    setPageAccess([]);
    setIsAuthenticated(false);
    window.location.href = `${AUTH_BASE}/auth/logout?t=${Date.now()}`;
  };

  const switchOrg = async (tenantId) => {
    try {
      // The Worker replaces the session cookie in this response.
      await apiClient.post(`${AUTH_BASE}/api/organizations/${tenantId}/switch`);
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      if (error.response?.status === 403) {
        window.location.href = '/';
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, permissions, pageAccess, isAuthenticated, loading, login, logout, switchOrg }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
